import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useMemo, useRef } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, Link as LinkIcon, Unlink,
  List, ListOrdered, Quote, Heading1, Heading2, Undo2, Redo2, Pilcrow,
} from "lucide-react";

/**
 * Extract the editable inner content from an email template.
 * Templates use a table wrapper for email-client compatibility. We freeze
 * the wrapper on save so users only touch the actual body content.
 *
 * Returns: { wrapperOpen, innerHtml, wrapperClose }
 * If no wrapper is detected, wrapper parts are empty and the whole html
 * becomes the inner content.
 */
function splitTemplate(fullHtml) {
  if (!fullHtml) return { wrapperOpen: "", innerHtml: "", wrapperClose: "" };
  // Look for the first <td...>...</td> block that wraps the message body.
  // Templates in this app follow: <table><tr><td style="padding:32px…">CONTENT</td></tr></table>
  const openMatch = fullHtml.match(/<td[^>]*>/i);
  if (!openMatch) return { wrapperOpen: "", innerHtml: fullHtml, wrapperClose: "" };
  const openIdx = openMatch.index + openMatch[0].length;
  const closeIdx = fullHtml.lastIndexOf("</td>");
  if (closeIdx <= openIdx) return { wrapperOpen: "", innerHtml: fullHtml, wrapperClose: "" };
  return {
    wrapperOpen: fullHtml.slice(0, openIdx),
    innerHtml: fullHtml.slice(openIdx, closeIdx),
    wrapperClose: fullHtml.slice(closeIdx),
  };
}

function ToolbarButton({ onClick, active, title, disabled, children, testId }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={
        "w-9 h-9 flex items-center justify-center border border-[#E8DCC8] transition-colors " +
        (active ? "bg-[#2C2621] text-[#FDFBF7]" : "bg-white text-[#4A4036] hover:bg-[#F5F0E6]") +
        (disabled ? " opacity-40 cursor-not-allowed" : "")
      }
    >
      {children}
    </button>
  );
}

/**
 * Visual email body editor.
 * - Preserves the outer <table>/<td> wrapper of the template on save.
 * - Lets user edit text, headings, links, lists, blockquote visually.
 * - Emits the FULL template HTML via onChange (parent stores it as-is).
 */
export default function EmailBodyEditor({ value, onChange, testId = "email-visual-editor" }) {
  const { wrapperOpen, innerHtml, wrapperClose } = useMemo(
    () => splitTemplate(value || ""),
    // Only re-split when the parent replaces the whole value (open template)
    // — not on each keystroke, or we'd fight our own editor content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const initialInner = useRef(innerHtml);
  const currentInner = useRef(innerHtml);
  const wrapperOpenRef = useRef(wrapperOpen);
  const wrapperCloseRef = useRef(wrapperClose);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { style: "color:#A3876A;text-decoration:underline" },
      }),
      Placeholder.configure({ placeholder: "Rédige ton email ici…" }),
    ],
    content: initialInner.current,
    editorProps: {
      attributes: {
        class:
          "min-h-[420px] max-h-[60vh] overflow-y-auto p-6 bg-[#FDFBF7] focus:outline-none " +
          "prose prose-sm max-w-none email-preview",
        style: "font-family:Georgia,serif;color:#2C2621",
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      currentInner.current = html;
      const full = wrapperOpenRef.current + html + wrapperCloseRef.current;
      onChange(full);
    },
  });

  // If the parent replaces `value` from outside (e.g. opening a different
  // template), re-split and reload the editor. Detect via a "template key"
  // — we treat a big change in `value` length or a different opening tag
  // as a template swap.
  useEffect(() => {
    if (!editor) return;
    const split = splitTemplate(value || "");
    // Only reload when the inner content is meaningfully different from what
    // the editor currently holds AND doesn't match the latest emitted HTML.
    if (split.innerHtml !== currentInner.current) {
      wrapperOpenRef.current = split.wrapperOpen;
      wrapperCloseRef.current = split.wrapperClose;
      editor.commands.setContent(split.innerHtml, false);
      currentInner.current = split.innerHtml;
    }
  }, [value, editor]);

  if (!editor) {
    return <div className="min-h-[420px] bg-[#FDFBF7] border border-[#E8DCC8] animate-pulse" />;
  }

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL du lien", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div data-testid={testId} className="border border-[#E8DCC8] bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-[#E8DCC8] bg-[#F5F0E6]">
        <ToolbarButton
          title="Titre principal"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          testId="visual-btn-h1"
        ><Heading1 size={16}/></ToolbarButton>
        <ToolbarButton
          title="Sous-titre"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        ><Heading2 size={16}/></ToolbarButton>
        <ToolbarButton
          title="Paragraphe"
          active={editor.isActive("paragraph")}
          onClick={() => editor.chain().focus().setParagraph().run()}
        ><Pilcrow size={16}/></ToolbarButton>
        <span className="w-px h-6 bg-[#E8DCC8] mx-1" />
        <ToolbarButton
          title="Gras"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          testId="visual-btn-bold"
        ><Bold size={16}/></ToolbarButton>
        <ToolbarButton
          title="Italique"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          testId="visual-btn-italic"
        ><Italic size={16}/></ToolbarButton>
        <ToolbarButton
          title="Souligné"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        ><UnderlineIcon size={16}/></ToolbarButton>
        <span className="w-px h-6 bg-[#E8DCC8] mx-1" />
        <ToolbarButton
          title="Liste à puces"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        ><List size={16}/></ToolbarButton>
        <ToolbarButton
          title="Liste numérotée"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        ><ListOrdered size={16}/></ToolbarButton>
        <ToolbarButton
          title="Citation"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        ><Quote size={16}/></ToolbarButton>
        <span className="w-px h-6 bg-[#E8DCC8] mx-1" />
        <ToolbarButton
          title="Insérer un lien"
          active={editor.isActive("link")}
          onClick={setLink}
        ><LinkIcon size={16}/></ToolbarButton>
        <ToolbarButton
          title="Retirer le lien"
          disabled={!editor.isActive("link")}
          onClick={() => editor.chain().focus().unsetLink().run()}
        ><Unlink size={16}/></ToolbarButton>
        <span className="w-px h-6 bg-[#E8DCC8] mx-1" />
        <ToolbarButton
          title="Annuler"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        ><Undo2 size={16}/></ToolbarButton>
        <ToolbarButton
          title="Refaire"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        ><Redo2 size={16}/></ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
